import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Bed, Plus, X, User, ChevronRight, Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

// Bed Inventory Visual Map (PMS Phase 1).
// Floor → Room → Bed grid with color-coded status; click a bed to view tenant
// or launch the "Assign Tenant" modal. Owner/admin only.
const STATUS_STYLE = {
  vacant:         { bg: "rgba(255,255,255,0.06)", ring: "rgba(255,255,255,0.2)", label: "Vacant", dot: "#ffffff66" },
  occupied:       { bg: "rgba(52,201,123,0.20)",  ring: "#34C97B",              label: "Occupied", dot: "#34C97B" },
  notice_period:  { bg: "rgba(245,158,11,0.20)",  ring: "#F59E0B",              label: "Notice", dot: "#F59E0B" },
  blocked:        { bg: "rgba(148,163,184,0.20)", ring: "#94A3B8",              label: "Blocked", dot: "#94A3B8" },
  maintenance:    { bg: "rgba(244,63,94,0.20)",   ring: "#F43F5E",              label: "Maintenance", dot: "#F43F5E" },
};

function GenerateModal({ propertyId, onClose, onDone }) {
  const [form, setForm] = useState({ floors: 2, rooms_per_floor: 4, capacity_per_room: 2, rent_per_bed: 8000, room_type: "double", ac: false, attached_bath: true });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/pms/properties/${propertyId}/beds/generate`, form);
      toast.success(`Generated ${data.created} beds across ${data.floors} floors`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="w-full max-w-md rounded-2xl p-6 border border-[#34C97B]/40" style={{background:"#0D1B2A"}} data-testid="generate-beds-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Generate Bed Inventory</h3>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white"><X size={18}/></button>
        </div>
        <p className="text-xs text-white/50 mb-4">Warning: this replaces existing beds. Only allowed when all beds are vacant.</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            ["floors","Floors"],["rooms_per_floor","Rooms per floor"],["capacity_per_room","Beds per room"],["rent_per_bed","Rent per bed (₹)"]
          ].map(([k,l]) => (
            <div key={k}>
              <label className="text-xs uppercase text-white/60 tracking-wider">{l}</label>
              <input type="number" min="1" required value={form[k]} onChange={(e)=>setForm({...form,[k]:parseFloat(e.target.value)})} className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white focus:border-[#34C97B] outline-none" data-testid={`gen-${k}`}/>
            </div>
          ))}
          <div className="col-span-2">
            <label className="text-xs uppercase text-white/60 tracking-wider">Room type</label>
            <select value={form.room_type} onChange={(e)=>setForm({...form,room_type:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white focus:border-[#34C97B] outline-none">
              <option value="single">Single</option><option value="double">Double</option><option value="triple">Triple</option><option value="dorm">Dorm</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-white/80"><input type="checkbox" checked={form.ac} onChange={(e)=>setForm({...form,ac:e.target.checked})}/> AC</label>
          <label className="flex items-center gap-2 text-sm text-white/80"><input type="checkbox" checked={form.attached_bath} onChange={(e)=>setForm({...form,attached_bath:e.target.checked})}/> Attached bath</label>
        </div>
        <button disabled={busy} className="mt-5 w-full bg-[#34C97B] hover:bg-[#22B36A] disabled:opacity-60 text-white font-semibold py-2.5 rounded-xl transition" data-testid="gen-submit">{busy?"Generating...":"Generate beds"}</button>
      </form>
    </div>
  );
}

function AssignTenantModal({ bed, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", occupation_type: "student", guardian_name: "", guardian_phone: "", rent: bed.rent });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/pms/tenants", { ...form, property_id: bed.property_id, bed_id: bed.id });
      toast.success("Tenant assigned"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="w-full max-w-md rounded-2xl p-6 border border-[#34C97B]/40 max-h-[90vh] overflow-y-auto" style={{background:"#0D1B2A"}} data-testid="assign-tenant-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-white">Assign Tenant</h3>
            <div className="text-xs text-white/50">Floor {bed.floor} · Room {bed.room_no} · Bed {bed.bed_no}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/60"><X size={18}/></button>
        </div>
        <div className="space-y-3">
          {[["name","Name",true],["phone","Phone",true],["email","Email",false],["guardian_name","Guardian name",false],["guardian_phone","Guardian phone",false]].map(([k,l,req]) => (
            <div key={k}>
              <label className="text-xs uppercase text-white/60 tracking-wider">{l}</label>
              <input required={req} type={k==="email"?"email":"text"} value={form[k]} onChange={(e)=>setForm({...form,[k]:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white focus:border-[#34C97B] outline-none" data-testid={`tenant-${k}`}/>
            </div>
          ))}
          <div>
            <label className="text-xs uppercase text-white/60 tracking-wider">Occupation</label>
            <select value={form.occupation_type} onChange={(e)=>setForm({...form,occupation_type:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white focus:border-[#34C97B] outline-none">
              <option value="student">Student</option><option value="working_professional">Working Professional</option><option value="short_stay">Short Stay</option><option value="freelancer">Freelancer</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase text-white/60 tracking-wider">Rent (₹/month)</label>
            <input type="number" value={form.rent} onChange={(e)=>setForm({...form,rent:parseFloat(e.target.value)})} className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white focus:border-[#34C97B] outline-none"/>
          </div>
        </div>
        <button disabled={busy} className="mt-5 w-full bg-[#34C97B] hover:bg-[#22B36A] disabled:opacity-60 text-white font-semibold py-2.5 rounded-xl transition" data-testid="assign-submit">{busy?"Saving...":"Assign tenant"}</button>
      </form>
    </div>
  );
}

export default function BedInventory() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [properties, setProperties] = useState([]);
  const [propId, setPropId] = useState(params.get("property") || "");
  const [data, setData] = useState(null);
  const [showGen, setShowGen] = useState(false);
  const [selectedBed, setSelectedBed] = useState(null);
  const [showAssign, setShowAssign] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (!["owner","admin","super_admin"].includes(user.role)) { nav("/dashboard"); return; }
    (async () => {
      const { data: props } = await api.get(`/properties?owner_id=${user.id}`);
      setProperties(props || []);
      if (!propId && props?.length) setPropId(props[0].id);
    })();
  }, [user, loading, nav, propId]);

  const load = async () => {
    if (!propId) return;
    try { const { data } = await api.get(`/pms/properties/${propId}/beds`); setData(data); }
    catch { setData({ beds: [], kpis: { total: 0, vacant: 0, occupied: 0, notice_period: 0, blocked: 0, occupancy_pct: 0 } }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propId]);

  const changeStatus = async (bedId, status) => {
    try { await api.put(`/pms/beds/${bedId}/status`, { status }); toast.success("Updated"); load(); setSelectedBed(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="panel-shell"><div className="p-20 text-white/60 text-center">Loading...</div></div>;

  // Group beds by floor → room
  const byFloor = {};
  (data?.beds || []).forEach(b => {
    byFloor[b.floor] = byFloor[b.floor] || {};
    byFloor[b.floor][b.room_no] = byFloor[b.floor][b.room_no] || [];
    byFloor[b.floor][b.room_no].push(b);
  });

  return (
    <div className="panel-shell" data-testid="bed-inventory">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#34C97B]">PMS · Inventory</div>
            <h1 className="font-display font-black text-3xl md:text-4xl text-white mt-1" style={{letterSpacing:"-0.02em"}}>Bed Inventory Map</h1>
            <p className="text-white/55 text-sm mt-1">Every bed. Every status. One view.</p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select value={propId} onChange={(e)=>setPropId(e.target.value)} className="px-3 py-2 rounded-xl bg-white/5 border border-white/15 text-white text-sm" data-testid="property-select">
              {properties.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
            <button onClick={()=>setShowGen(true)} className="inline-flex items-center gap-1.5 bg-[#34C97B] hover:bg-[#22B36A] text-white px-4 py-2 rounded-xl text-sm font-semibold transition" data-testid="generate-btn">
              <Plus size={14}/> Generate
            </button>
          </div>
        </div>

        {/* KPIs */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {[
              ["Total", data.kpis.total, "#94A3B8"],
              ["Occupied", data.kpis.occupied, "#34C97B"],
              ["Vacant", data.kpis.vacant, "#F1F5F9"],
              ["Notice", data.kpis.notice_period, "#F59E0B"],
              ["Occupancy %", data.kpis.occupancy_pct, "#34C97B"],
            ].map(([l, v, c]) => (
              <div key={l} className="rounded-2xl p-4 border border-[#34C97B]/25" style={{background:"rgba(255,255,255,0.04)", backdropFilter:"blur(14px)"}}>
                <div className="text-xs uppercase tracking-wider text-white/55">{l}</div>
                <div className="text-2xl md:text-3xl font-black mt-1" style={{color:c}}>{v}{l==="Occupancy %"?"%":""}</div>
              </div>
            ))}
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mb-5 text-xs">
          {Object.entries(STATUS_STYLE).map(([k, s]) => (
            <span key={k} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-white/70" style={{background:s.bg}}>
              <span className="w-2 h-2 rounded-full" style={{background:s.dot}}></span>{s.label}
            </span>
          ))}
        </div>

        {/* Floor grid */}
        {(!data || data.beds.length === 0) ? (
          <div className="rounded-2xl p-10 text-center border border-[#34C97B]/25" style={{background:"rgba(255,255,255,0.04)"}}>
            <Building2 size={44} className="text-[#34C97B]/60 mx-auto"/>
            <div className="text-lg font-semibold text-white mt-3">No beds yet</div>
            <p className="text-sm text-white/55 mt-1">Click Generate to bulk-create bed inventory for this property.</p>
          </div>
        ) : (
          Object.keys(byFloor).sort((a,b)=>+a-+b).map(floor => (
            <div key={floor} className="rounded-2xl p-5 border border-[#34C97B]/25 mb-5" style={{background:"rgba(255,255,255,0.04)", backdropFilter:"blur(14px)"}}>
              <div className="text-xs uppercase tracking-widest text-white/50 mb-4">Floor {floor}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.keys(byFloor[floor]).sort().map(rn => (
                  <div key={rn} className="rounded-xl p-3 border border-white/10 bg-white/[0.02]">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-bold text-white">Room {rn}</div>
                      <span className="text-[10px] uppercase tracking-widest text-white/40">{byFloor[floor][rn][0].room_type}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {byFloor[floor][rn].map(b => {
                        const s = STATUS_STYLE[b.status] || STATUS_STYLE.vacant;
                        return (
                          <button
                            key={b.id}
                            onClick={()=>setSelectedBed(b)}
                            className="flex flex-col items-center justify-center w-14 h-14 rounded-lg transition hover:scale-105"
                            style={{background:s.bg, border:`1.5px solid ${s.ring}`}}
                            data-testid={`bed-${b.room_no}-${b.bed_no}`}
                            title={`${s.label}${b.tenant?" — "+b.tenant.name:""}`}
                          >
                            <Bed size={16} style={{color:s.dot}}/>
                            <span className="text-[10px] font-bold text-white/80 mt-0.5">{b.bed_no}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {showGen && <GenerateModal propertyId={propId} onClose={()=>setShowGen(false)} onDone={()=>{setShowGen(false); load();}}/>}
      {showAssign && selectedBed && <AssignTenantModal bed={selectedBed} onClose={()=>setShowAssign(false)} onDone={()=>{setShowAssign(false); setSelectedBed(null); load();}}/>}

      {/* Bed detail drawer */}
      {selectedBed && !showAssign && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 flex items-end md:items-center justify-center p-4" onClick={()=>setSelectedBed(null)}>
          <div onClick={(e)=>e.stopPropagation()} className="w-full max-w-md rounded-2xl p-6 border border-[#34C97B]/40" style={{background:"#0D1B2A"}} data-testid="bed-detail">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-white/50">Bed detail</div>
                <div className="text-lg font-bold text-white">Floor {selectedBed.floor} · Room {selectedBed.room_no} · Bed {selectedBed.bed_no}</div>
              </div>
              <button onClick={()=>setSelectedBed(null)} className="text-white/60"><X size={18}/></button>
            </div>
            {selectedBed.tenant ? (
              <div className="rounded-xl p-4 bg-[#34C97B]/10 border border-[#34C97B]/30">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#34C97B]/25 grid place-items-center"><User size={18} className="text-[#34C97B]"/></div>
                  <div>
                    <div className="font-bold text-white">{selectedBed.tenant.name}</div>
                    <div className="text-xs text-white/60">{selectedBed.tenant.phone} · {selectedBed.tenant.occupation_type||"tenant"}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                  <div><div className="text-white/50">Rent</div><div className="text-white font-bold">₹{selectedBed.tenant.rent||selectedBed.rent}</div></div>
                  <div><div className="text-white/50">Move-in</div><div className="text-white font-bold">{(selectedBed.tenant.move_in_date||"").slice(0,10)}</div></div>
                </div>
                <button onClick={()=>changeStatus(selectedBed.id, "vacant")} className="mt-4 w-full py-2 rounded-lg bg-white/5 border border-white/15 text-white/80 text-sm hover:border-rose-500/50 hover:text-rose-300 transition" data-testid="checkout-btn">
                  Mark vacant (checkout)
                </button>
              </div>
            ) : (
              <>
                <div className="text-sm text-white/70 mb-4">This bed is <strong style={{color:STATUS_STYLE[selectedBed.status].dot}}>{STATUS_STYLE[selectedBed.status].label}</strong>. Choose an action:</div>
                <div className="space-y-2">
                  <button onClick={()=>setShowAssign(true)} className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-[#34C97B]/15 border border-[#34C97B]/40 text-[#34C97B] font-semibold hover:bg-[#34C97B]/25 transition" data-testid="assign-tenant-btn">
                    <span>Assign a tenant</span><ChevronRight size={16}/>
                  </button>
                  {["blocked","maintenance","vacant"].filter(s=>s!==selectedBed.status).map(s => (
                    <button key={s} onClick={()=>changeStatus(selectedBed.id, s)} className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white/80 hover:border-white/25 transition capitalize" data-testid={`mark-${s}`}>
                      <span>Mark {STATUS_STYLE[s].label}</span><ChevronRight size={16}/>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
